<script setup>
import { ref, computed } from 'vue';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import api from 'dashboard/api/aceleres/iaRodizios';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  evento: { type: Object, default: null },
  teamId: { type: [Number, String], default: null },
});
const emit = defineEmits(['close', 'saved']);

const isEdit = computed(() => !!props.evento?.id);

const toLocalDatetime = iso => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (e) {
    return '';
  }
};

const form = ref({
  name: props.evento?.name || '',
  description: props.evento?.description || '',
  expires_at: toLocalDatetime(props.evento?.expires_at),
  is_active: props.evento?.is_active ?? true,
  file: null,
});

const saving = ref(false);

const onFileChange = e => {
  form.value.file = e.target.files?.[0] || null;
};

const submit = async () => {
  if (!form.value.name.trim()) {
    useAlert('Informe o nome do evento');
    return;
  }
  if (!form.value.expires_at) {
    useAlert('Informe a data de expiração');
    return;
  }
  saving.value = true;
  try {
    const payload = { ...form.value, expires_at: new Date(form.value.expires_at).toISOString() };
    if (isEdit.value) {
      await api.updateEvento(props.evento.id, payload, props.teamId);
    } else {
      await api.createEvento(payload, props.teamId);
    }
    useAlert(isEdit.value ? 'Evento atualizado' : 'Evento criado');
    emit('saved');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao salvar evento');
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <Modal :show="true" :on-close="() => emit('close')" size="medium">
    <div class="p-6 flex flex-col gap-4 w-full">
      <h2 class="text-display-small text-n-slate-12 font-medium m-0">
        {{ isEdit ? 'Editar evento' : 'Novo evento' }}
      </h2>

      <div class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Nome *</label>
        <input v-model="form.name" type="text" maxlength="200"
          class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Descrição (opcional)</label>
        <textarea v-model="form.description" rows="3"
          class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
      </div>

      <div class="flex gap-4">
        <div class="flex flex-col gap-1.5 flex-1">
          <label class="text-body-small text-n-slate-12 font-medium">Data de expiração *</label>
          <input v-model="form.expires_at" type="datetime-local"
            class="border border-n-strong rounded-md py-2 px-3 bg-n-background text-n-slate-12" />
        </div>
        <div class="flex items-end pb-2">
          <label class="flex items-center gap-2 text-body-small text-n-slate-12">
            <input v-model="form.is_active" type="checkbox" />
            Ativo
          </label>
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-body-small text-n-slate-12 font-medium">Arquivo (opcional)</label>
        <input type="file" @change="onFileChange" />
        <span v-if="props.evento?.file_name && !form.file" class="text-body-small text-n-slate-11">
          Arquivo atual: {{ props.evento.file_name }}
        </span>
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <Button label="Cancelar" variant="ghost" @click="emit('close')" />
        <Button :label="isEdit ? 'Salvar alterações' : 'Criar evento'"
                :is-loading="saving" @click="submit" />
      </div>
    </div>
  </Modal>
</template>
